import React from 'react';
import {
  ActionDropDown, ActionDropdownOption, Badge, BadgeColor, ConfirmModal, ICON_ALERT_CIRCLE_OUTLINE,
  ICON_CHECK_CIRCLE_OUTLINE, ICON_CLOSE_CIRCLE_OUTLINE, ICON_PROGRESS_CHECK,
  Portal, useModal, useNotifications,
} from '@doveit/bricks';
import { formatDate } from '@doveit/hammer';
import { HRInterview } from '../../../providers/api/dtos';
import { updateHRCalendarEvent } from '../../../providers/api/hr/hrProvider';
import { useComputedHRCalendarEventStatus } from '../../hooks/use-computed-hr-calendar-event-status/useComputedHRCalendarEventStatus';
import { HRInterviewComputedStatus, HRInterviewStatus } from '../../constants';
import { HRInterviewStatusType } from '../../types';

export const DONE_HR_INTERVIEW_SUCCESS_MESSAGE = 'Il colloquio è stato segnato come fatto con successo.';
export const CANCEL_HR_INTERVIEW_SUCCESS_MESSAGE = 'Il colloquio è stato annullato con successo.';
export const UPDATE_HR_INTERVIEW_STATUS_ERROR_MESSAGE = 'Non è stato possibile aggiornare lo stato del colloquio.';

export interface UpdateHRInterviewStatusProps {
  hrInterview: HRInterview,
  showAsBadge?: boolean,
  showIcon?: boolean,
  emphasis?: 'low' | 'high',
  onSuccess?: (updatedHRInterview: HRInterview) => void,
}

interface StatusProps {
  label: string,
  icon: string,
  color: BadgeColor,
  showAsBadge: boolean,
  actions?: ActionDropdownOption[],
}

const UpdateHRInterviewStatus: React.FC<UpdateHRInterviewStatusProps> = ({
  hrInterview,
  showAsBadge = false,
  showIcon = false,
  emphasis = 'low',
  onSuccess,
}) => {
  const updateModal = useModal<{ newStatus: HRInterviewStatusType, hrInterview: HRInterview }>();
  const { addSuccess, addError } = useNotifications();

  const { data: computedStatusData } = useComputedHRCalendarEventStatus(hrInterview);

  const onClickAction = React.useCallback((newStatus: HRInterviewStatusType) => () => {
    updateModal.open({ newStatus, hrInterview });
  }, [hrInterview, updateModal]);

  const updateInterview = React.useCallback((status: HRInterviewStatusType) => async () => {
    try {
      const updatedHRInterview = await updateHRCalendarEvent<HRInterview>(hrInterview.id!, {
        ...hrInterview,
        status,
      });

      updateModal.close();
      addSuccess(status === HRInterviewStatus.Done ? DONE_HR_INTERVIEW_SUCCESS_MESSAGE : CANCEL_HR_INTERVIEW_SUCCESS_MESSAGE);
      onSuccess?.(updatedHRInterview);
    } catch {
      addError(UPDATE_HR_INTERVIEW_STATUS_ERROR_MESSAGE);
    }
  }, [addError, addSuccess, hrInterview, onSuccess, updateModal]);

  const modalMessage = React.useMemo(() => {
    if (updateModal.data) {
      const formattedDate = formatDate(new Date(updateModal.data.hrInterview.date), { time: true });
      const statusLabel = updateModal.data.newStatus === HRInterviewStatus.Done ? 'segnato come fatto' : 'annullato';
      return `Confermando l'operazione il colloquio previsto per il giorno ${formattedDate} sarà ${statusLabel}.`;
    }
    return '';
  }, [updateModal.data]);

  const statusProps: StatusProps = React.useMemo(() => {
    switch (computedStatusData?.status) {
      case HRInterviewComputedStatus.Todo:
        return {
          label: 'Da fare',
          icon: ICON_PROGRESS_CHECK,
          color: 'info',
          showAsBadge,
          actions: [{
            key: 'cancel',
            label: 'Annulla colloquio',
            onClick: onClickAction(HRInterviewStatus.Cancelled),
          }],
        };
      case HRInterviewComputedStatus.ToProcess:
        return {
          label: 'Da esitare',
          icon: ICON_ALERT_CIRCLE_OUTLINE,
          color: 'warning',
          showAsBadge,
          actions: [{
            key: 'done',
            label: 'Segna come fatto',
            onClick: onClickAction(HRInterviewStatus.Done),
          }, {
            key: 'cancel',
            label: 'Segna come annullato',
            onClick: onClickAction(HRInterviewStatus.Cancelled),
          }],
        };
      case HRInterviewComputedStatus.Done:
        return {
          label: 'Fatto',
          icon: ICON_CHECK_CIRCLE_OUTLINE,
          color: 'success',
          showAsBadge: true,
        };
      case HRInterviewComputedStatus.Cancelled:
      default:
        return {
          label: 'Annullato',
          icon: ICON_CLOSE_CIRCLE_OUTLINE,
          color: 'critical',
          showAsBadge: true,
        };
    }
  }, [computedStatusData?.status, onClickAction, showAsBadge]);

  return (
    <>
      {statusProps.showAsBadge && (
        <Badge
          aria-label="Badge per visualizzare lo stato del colloquio"
          label={statusProps.label}
          icon={showIcon ? statusProps.icon : undefined}
          color={statusProps.color}
          emphasis={emphasis}
          size="XS"
        />
      )}

      {!statusProps.showAsBadge && (
        <ActionDropDown
          aria-label="Azione per aggiornare lo stato del colloquio"
          label={statusProps.label}
          iconLeft={showIcon ? { path: statusProps.icon } : undefined}
          color={statusProps.color}
          size="XS"
          emphasis={emphasis}
          options={statusProps.actions}
        />
      )}

      {updateModal.data && (
        <Portal>
          <ConfirmModal
            {...updateModal}
            title={updateModal.data.newStatus === HRInterviewStatus.Done ? 'Conferma esito colloquio' : 'Conferma annullamento colloquio'}
            onConfirm={updateInterview(updateModal.data.newStatus)}
            onAbort={updateModal.close}
            dismissable={false}
            aria-label="Modale per aggiornare lo stato del colloquio"
          >
            {modalMessage}
          </ConfirmModal>
        </Portal>
      )}
    </>
  );
};

export default UpdateHRInterviewStatus;
